import * as _ from "lodash";

import { ExtensionBase } from "../extensionBase";
import { ModelApi } from "../../api/modelApi";
import { viewerOptions, addDndListener } from "../../viewer";
import * as SmartDrawings from "../../smartDrawings";
import { DnDFileController } from "../../tools/dndFileController";
import { TextControl } from "../../controls/textControl";
import { ViewerUtils } from "../../common/viewerUtils";
import { CustomColorExtension } from "../customColor/customColorExtension";
import { UiUtils } from "../../common/uiUtils";
import { BuildOrderToolbar } from "./buildOrderToolbar";
import { NodeAnimator } from "./nodeAnimator";
import { SettingsUtils } from "../settings/settingsUtils";

export type Point = { x: number, y: number, z: number };
export type Assembly = { Id: string, Parts?: string[], Path?: Point[] };
export type AssemblyModel = { Assemblies: Assembly[] };

export type AssemblyMap = { [key: string]: Assembly[]; };

export type Step = { PlannedToInstall: Assembly[] };
export type BuildPlanModel = { Steps: Step[] };

export class BuildOrderExtension extends ExtensionBase {

    static extensionName = "BuildOrderExtension";
    static highlightColor = new THREE.Vector4(1, 0.6, 0, 1);

    private subToolbar: Autodesk.Viewing.UI.ControlGroup;
    private orderButton: Autodesk.Viewing.UI.Button;
    private buildOrderToolbar: BuildOrderToolbar;
  
    private assemblyMap: AssemblyMap;
    private buildPlanModel: BuildPlanModel;
    private position = 0;
    private intervalId: number;

    private nodeAnimator: NodeAnimator;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: any) {
        super(viewer, options);
        this.nodeAnimator = new NodeAnimator(viewer);
        this.onModelRootLoaded = this.onModelRootLoaded.bind(this);
    }

    static registerExtension() {
        super.registerExtension(BuildOrderExtension.extensionName, BuildOrderExtension);
    }

    private attachDnd() {
        addDndListener((file, result) => {
            const lower: string = file.name.toLowerCase();
            if (lower.indexOf("assembly") >= 0) {
                this.validateAssemblyModel(result);
                return true;
            } else if (lower.indexOf("buildplan") >= 0) {
                this.validateBuildPlanModel(result);
                return true;
            }
            return false;
        });
    }

    private validateAssemblyModel(model: AssemblyModel) {
        this.position = 0;
        if (!_.isEmpty(model.Assemblies)) {
            this.assemblyMap = _.groupBy(model.Assemblies, "Id");
        } else {
            this.assemblyMap = null;
        }
    }

    private validateBuildPlanModel(model: BuildPlanModel) {
        this.position = 0;
        if (!_.isEmpty(model.Steps)) {
            this.buildPlanModel = model;
            UiUtils.setButtonEnabled(this.orderButton, true);
            this.autoPlay(false);
            this.buildOrderToolbar.updateUi(this.position, this.modelSize(), this.canGoBack(), this.canGoForward(), this.autoPlayOn());
            if (this.buildOrderToolbar.buildOrderActive()) {
                this.setNodeStates();
            }
        } else {
            this.buildPlanModel = null;
            UiUtils.setButtonEnabled(this.orderButton, false);
        }
    }

    private setBuildOrder = async (buildOrder: SmartDrawings.BuildOrder) => {
        this.loadAssembly(buildOrder);
        this.loadBuildPlan(buildOrder);
    }

    private async loadAssembly(buildOrder: SmartDrawings.BuildOrder) {
        const { data } = await ModelApi.getModelFile(viewerOptions.guid, buildOrder.assemblyFile);
        this.validateAssemblyModel(data);
    }

    private async loadBuildPlan(buildOrder: SmartDrawings.BuildOrder) {
        const { data } = await ModelApi.getModelFile(viewerOptions.guid, buildOrder.buildPlanFile);
        this.validateBuildPlanModel(data);
    }

    onToolbarCreated(): void {
        this.orderButton = UiUtils.createButton({
            id: "SmartDrawings.BuildOrderButton",
            tooltip: "Build order",
            iconClass: "ion-md-construct",
            onClick: () => {
                this.toggleBuildOrderToolbar();
            }
        });
        UiUtils.setButtonEnabled(this.orderButton, !_.isEmpty(viewerOptions.buildOrders));
        this.subToolbar = this.getControlGroup("SmartDrawings.Extensions");
        this.subToolbar.addControl(this.orderButton);

        this.buildOrderToolbar = new BuildOrderToolbar(this.viewer, this.subToolbar, this.traverseBuildOrder, this.autoPlay, this.setBuildOrder);
        if (!_.isEmpty(viewerOptions.buildOrders)) {
            this.setBuildOrder(viewerOptions.buildOrders[0]);
        }
    }

    private toggleBuildOrderToolbar() {
        if (this.buildOrderToolbar.buildOrderActive()) {
            this.buildOrderToolbar.hide();
            UiUtils.setButtonEnabled(this.orderButton, true);
            this.viewer.showAll();
            this.nodeAnimator.stop();
            this.autoPlay(false);
        } else {
            this.buildOrderToolbar.show();
            this.orderButton.setState(Autodesk.Viewing.UI.Button.State.ACTIVE);
            this.setNodeStates();
        }
    }

    private modelSize() {
        return this.buildPlanModel ? this.buildPlanModel.Steps.length : 0;
    }

    private canGoBack() {
        return this.buildPlanModel != null && this.position > 0;
    }

    private canGoForward() {
        return this.buildPlanModel != null && this.position < this.buildPlanModel.Steps.length - 1;
    }

    private traverseBuildOrder = (step: number) => {
        const newPosition = this.position + step;
        if (newPosition > 0) {
            if (newPosition < this.buildPlanModel.Steps.length) {
                this.position = newPosition;
            } else {
                this.position = this.buildPlanModel.Steps.length - 1;
                this.autoPlay(false);
            }
        } else {
            this.position = 0;
        }
        this.buildOrderToolbar.updateUi(this.position, this.modelSize(), this.canGoBack(), this.canGoForward(), this.autoPlayOn());

        this.setNodeStates();
    }

    private resetBuildOrder() {
        this.position = 0;
        this.buildOrderToolbar.updateUi(this.position, this.modelSize(), this.canGoBack(), this.canGoForward(), this.autoPlayOn());
        this.setNodeStates();
    }

    private autoPlay = (enabled: boolean) => {
        if (this.autoPlayOn()) {
            window.clearInterval(this.intervalId);
            this.intervalId = null;
        }
        if (enabled) {
            if (!this.canGoForward()) {
                this.resetBuildOrder();
            }
            const interval = SettingsUtils.loadSingleSetting("buildOrderInterval");
            this.intervalId = window.setInterval(() => this.traverseBuildOrder(1), interval != null ? interval * 1000 : viewerOptions.uiConstants.buildOrderInterval);
        } 
    }

    private autoPlayOn() {
        return this.intervalId != null;
    }

    private getAllParts(assemblies: Assembly[]): string[] {
        return _.reduce(assemblies,
            (acc, assembly) => {
                return [...acc, ...(assembly.Parts != null ? assembly.Parts : [assembly.Id])];
            },
            []);
    }

    getStepAssemblies(step: Step): Assembly[] {
        return step.PlannedToInstall.map(pti => {
            if (this.assemblyMap != null) {
                return this.assemblyMap[pti.Id][0];
            } else {
                return pti;
            }
        });
    }

    private setNodeStates() {
        const currentStep = this.buildPlanModel.Steps[this.position];
        const pastSteps = _.slice(this.buildPlanModel.Steps, 0, this.position);

        const currentAssemblies = this.getStepAssemblies(currentStep);
        const pastAssemblies = _.flatten<Assembly>(pastSteps.map(step => this.getStepAssemblies(step)));

        const fadedParts = this.getAllParts(pastAssemblies);
        const visibleParts = [...fadedParts, ...this.getAllParts(currentAssemblies)];

        // Combo model not supported.
        this.viewer.isolate(visibleParts.map(p => viewerOptions.idMap.nodeId(viewerOptions.guid, p)));

        const currentParts = _.map(this.getAllParts(currentAssemblies), p => viewerOptions.idMap.nodeId(viewerOptions.guid, p));
        //const currentPartsWithChildren = [...currentParts, ...ViewerUtils.getLeafNodes(currentParts, this.viewer)];
        this.viewer.select(currentParts);

        this.nodeAnimator.stop();
        //this.nodeAnimator.start(currentPartsWithChildren, this.position, currentAssembly.Path);
    }

    load(): boolean {
        console.log("BuildOrderExtension is loaded!");
        this.attachDnd();
        return super.load();
    };

    unload(): boolean {
        //this.viewer.getToolbar(false).removeControl(this.subToolbar);
        return super.unload();
    };

}